import { Box } from '@mui/material';
import {
  applySearchToData,
  IWork,
  LeftDrawerScrollContentContainer,
  PageDrawerSearchEmpty,
  PageDrawerToolbar,
  PageDrawerToolbarSearchBarHeight,
  Sorting,
  TOOLBAR_HEIGHT,
  useFixedHeadersStore,
  useHeaderHeight,
  usePageDrawerToolbar,
  useWorkPageStore,
} from '@diana/core';
import { useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import { defaults, groupBy as _groupBy } from 'lodash';
import { NavigationGroup } from './NavigationGroup';

interface INavigationRenderer {
  works: IWork[];
  labelKey?: string;
  dateKey?: string;
  dataPath?: string | null;
  dataLabel?: string | null;
  isEntities?: boolean;
  groupBy?: string;
  sortBy?: string;
  sortType?: string;
  searchIn?: string[];
  defaultSorting?: Sorting;
  statusIcon?: string;
  statusIconColor?:
    | 'warning'
    | 'inherit'
    | 'disabled'
    | 'action'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | undefined;
  statusIconShowsWhen?: string;
  statusIconTooltipText?: string;
  untitledLabel?: string;
  untitledGroupLabel?: string;
  peoplePage?: boolean;
}

function applySearch(works: IWork[], searchIn: string[], keyword: string) {
  return applySearchToData(works, searchIn, keyword);
}

function applySorting(works: any[], sortBy: string, sorting: Sorting, sortType: string) {
  return works.sort((a, b) => {
    if (sortType === 'string') {
      const stringA = typeof a[sortBy] === 'string' ? a[sortBy].toLowerCase() : '';
      const stringB = typeof b[sortBy] === 'string' ? b[sortBy].toLowerCase() : '';
      if (stringA < stringB) return sorting === Sorting.Desc ? 1 : -1;
      if (stringA > stringB) return sorting === Sorting.Desc ? -1 : 1;
      return 0;
    } else {
      const dateA = DateTime.fromISO(a[sortBy]);
      const dateB = DateTime.fromISO(b[sortBy]);
      return sorting === Sorting.Desc ? +dateB - +dateA : +dateA - +dateB;
    }
  });
}

function group(works: IWork[], groupBy: string, noGroup: string) {
  return _groupBy(works, (work: any) => {
    if (work[groupBy]) return work[groupBy];
    return noGroup;
  });
}

const SearchBarHeight = PageDrawerToolbarSearchBarHeight + 2.5;

export function NavigationRenderer({
  works = [],
  isEntities,
  labelKey = 'displayName',
  dateKey = 'lastModified',
  dataPath = null,
  dataLabel = null,
  groupBy = 'group',
  sortBy = dateKey,
  searchIn = [labelKey],
  defaultSorting = Sorting.Desc,
  untitledLabel = 'Untitled',
  untitledGroupLabel = 'No Group',
  sortType = 'date',
  peoplePage,
  ...iconProps
}: INavigationRenderer) {
  const containerHeaderHeight = useHeaderHeight() + TOOLBAR_HEIGHT + 11 + SearchBarHeight;
  const toolbarProps = usePageDrawerToolbar({
    config: { showSorting: true, defaultSorting: Sorting.Desc },
  });

  const { keyword, sorting } = toolbarProps;
  const groupedWorks = useMemo(() => {
    const withUntitled = works.map((w: IWork) =>
      defaults({}, w, {
        [labelKey]: untitledLabel,
        [groupBy]: untitledGroupLabel,
      }),
    );

    return Object.entries(
      group(
        applySorting(applySearch(withUntitled, searchIn, keyword), sortBy, sorting, sortType),
        groupBy,
        untitledGroupLabel,
      ),
    );
  }, [
    groupBy,
    keyword,
    labelKey,
    searchIn,
    sortBy,
    sorting,
    untitledGroupLabel,
    untitledLabel,
    works,
    sortType,
  ]);
  const isAnyGroup = groupedWorks
    .map(([group]) => group)
    .filter((group) => group !== untitledGroupLabel).length;
  const showGroup = !peoplePage && !isAnyGroup;

  const content = !!groupedWorks.length ? (
    groupedWorks.map(([group, works], i) => (
      <NavigationGroup
        key={i}
        isEntities={isEntities}
        group={showGroup ? group : ''}
        works={works}
        labelKey={labelKey}
        dateKey={dateKey}
        dataPath={dataPath}
        dataLabel={dataLabel}
        untitledLabel={untitledLabel}
        peoplePage={peoplePage}
        {...iconProps}
      />
    ))
  ) : (
    <PageDrawerSearchEmpty />
  );

  const id = useWorkPageStore((state) => state.work?.id);
  const containerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    const item = document.getElementById(`sidenav-${id}`);
    if (!item || !containerRef.current) return;
    const headerHeight = useFixedHeadersStore.getState().getTotalOffset();
    containerRef.current.scrollTop =
      item.offsetTop - headerHeight - SearchBarHeight - item.getBoundingClientRect().height - 4;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <PageDrawerToolbar {...toolbarProps} disableSorting={!groupedWorks.length} />
      <LeftDrawerScrollContentContainer
        ref={containerRef}
        sx={{ height: `calc(100vh - ${containerHeaderHeight}px)` }}
        id="navigation-sidebar"
      >
        <Box sx={{ m: 2, mt: 1 }}>{content}</Box>
      </LeftDrawerScrollContentContainer>
    </Box>
  );
}
